<template>
	<div>
		<!-- page content -->
		<v-row dense class="fill-height ma-0">
			<v-col
				md="3"
				cols="12"
				class="tree-container pt-2 rounded-lg grey lighten-3"
			>
				<!-- search -->
				<div class="d-flex align-center mb-2 px-1">
					<v-autocomplete
						v-model="openItemId"
						:label="$T('inputs.account-name')"
						:items="accounts"
						:item-text="item => `${item.accountingNo}-${item.name}`"
						item-value="id"
						dense
						outlined
						hide-details
						class="me-1"
						@change="activeNode = [openItemId]; selectActiveAccount(openItemId)"
					></v-autocomplete>

					<v-btn
						v-if="permissions.accounts.isReadWrite"
						depressed
						height="32"
						min-width="0"
						color="primary"
						class="px-2"
						:disabled="disableState"
						:to="{
							name: 'account',
							query: activeNode.length ? { subOf: $route.params.id } : {}
						}"
					>
						<v-icon>$add</v-icon>
						{{$T('add')}}
					</v-btn>

					<v-tooltip>
						<template v-slot:activator="{ attrs, on }">
							<v-btn
								v-bind="attrs"
								v-on="on"
								icon
								small
								color="blue darken-2"
								class="ms-1"
								:disabled="disableState"
								@click="showArchived = !showArchived"
							>
								<v-icon>mdi-{{!showArchived ? 'archive-eye-outline' : 'archive-eye'}}</v-icon>
							</v-btn>
						</template>

						{{$T('show-archived')}}
					</v-tooltip>
				</div>

				<!-- tree -->
				<v-progress-linear v-if="loading" indeterminate/>
				<bee-treeview
					v-else
					:items="accountsTree"
					dense
					hoverable
					activatable
					class="items-tree"
					root-class="primary--text font-weight-medium"
					active-class="success--text"
					expand-icon="mdi-chevron-down"
					:open-item.sync="openItemId"
					:active.sync="activeNode"
					:open.sync="openNodes"
					calculate-width
					:item-class="item => item.isArchived ? 'blue--text text--darken-4' : null"
					:disabled="disableState"
					:calculate-threshold="0.9"
					:disable-context-menu="!permissions.accounts.isReadWrite"
					:context-menu-items="permissions.accounts.isReadWrite ? contextmenuItems : []"
					@context-menu-edit="item => {
						activeNode = [item.id];
						$router.replace({ name: 'account', params: { id: item.id } })
					}"
					@context-menu-delete="item => {
						activeNode = [item.id];
						$router.replace({ name: 'account', params: { id: item.id }, query: { preview: 1, delete: 1 } })
					}"
					@click="selectActiveAccount"
				>
					<template v-slot:prepend-label="{ item }">
						{{item.accountingNo + '-'}}
					</template>
					<template v-slot:append="{ item }">
						<v-icon v-if="item.isArchived" color="primary darken-4">mdi-archive</v-icon>
					</template>
				</bee-treeview>
			</v-col>
			<v-col md="9" cols="12">
				<div
					v-if="$route.name === 'accounts'"
					class="d-flex flex-column justify-center align-center fill-height"
				>
					<v-img
						src="@/assets/accountant-outline.png"
						max-width="200"
						max-height="240"
						class="mb-2"
					/>
					<p
						class="text-h6 grey--text text--darken-2 ma-0"
					>{{$T('please-select-an-account')}}</p>
				</div>
				<router-view
					v-else
					@change-disable-state="e => disableState = e"
					@update-accounts="updateAccounts"
					class="pa-3 py-md-0"
				></router-view>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import { mapState } from 'vuex';
export default {
	name: 'Accounts',

	data() {
		return {
			loading: false,

			disableState: false,

			showArchived: false,

			openItemId: null,
			activeNode: [],
			openNodes: [],
		}
	},

	watch: {
		showArchived() {
			this.fetchAccountsTree(true);
		},
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			accounts: state => state.accounts.accounts.filter(c => !c.isArchived) || [],
			accountsTree: state => state.accounts.accountsTree || [],
		}),
		contextmenuItems() {
			return [
				{
					title: this.$T('edit'),
					event: 'edit',
					class: 'warning-color--text',
					icon: '$edit',
					iconColor: 'warning-color',
					iconClass: 'me-1'
				},
				{
					title: this.$T('delete'),
					event: 'delete',
					class: 'alert-color--text',
					icon: '$delete',
					iconColor: 'alert-color',
					iconClass: 'me-1'
				}
			]
		},
	},

	methods: {
		selectActiveAccount(data) {
			let item = data;
			if (typeof item === 'number') {
				item = this.accounts.find(c => c.id === item);
			}
			
			this.openNodes.push(item.id);
			if (this.$route.params.id !== item.id) {
				this.$router.replace({
					name: 'account',
					params: { id: item.id },
					query: { preview: 1 },
				})
			}
		},

		updateAccounts(id) {
			this.loading = true;
			Promise.all([
				this.$store.dispatch('accounts/fetchAll'),
				this.$store.dispatch('accounts/fetchTree', { withArchived: this.showArchived }),
				this.$store.dispatch('configs/fetchConfigAccounts')
			]).then(() => {
				if (id && !['delete', 'archive'].includes(id)) {
					this.openItemId = id;
					this.activeNode.push(id);
					this.$router.replace({ name: 'account', params: { id }, query: { preview: 1 } });
				} else {
					this.$router.replace({ name: 'accounts' });
				}
			}).finally(() => {
				this.loading = false;
			})	
		},

		fetchAccountsTree(forceToFetch = false) {
			if (this.accountsTree.length && !forceToFetch) return;
			this.loading = true;
			this.$store.dispatch('accounts/fetchTree', { withArchived: this.showArchived }).finally(() => {
				this.loading = false;
			})
		}
	},

	created() {
		this.fetchAccountsTree();
		
		if (this.$route.params.id) {
			this.openItemId = this.$route.params.id;
			this.activeNode.push(this.$route.params.id);
		}
	}
}
</script>

<style>

</style>